import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { closeAccountModal, selectAccountModal, setMode } from '../accountModalSlice';
import LoginPanel from './LoginPanel';
import RegisterPanel from './RegisterPanel';
import ForgotPanel from './ForgotPanel';

export default function AccountModal() {
  const { t } = useTranslation();
  const { open, mode } = useAppSelector(selectAccountModal);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dispatch(closeAccountModal()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dispatch]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('account.modal.ariaLabel')}
      className="fixed inset-0 z-[1100] flex items-center justify-center p-3 md:p-6 bg-ink/75 backdrop-blur-[6px] animate-[modalOverlay_180ms_ease-out]"
    >
      <div
        className="absolute inset-0"
        onClick={() => dispatch(closeAccountModal())}
        aria-hidden="true"
      />
      <div
        className="relative bg-paper border-2 border-ink rounded-btn w-full max-w-[440px] max-h-[calc(100dvh-24px)] md:max-h-[calc(100dvh-48px)] flex flex-col overflow-hidden animate-[modalPunchIn_280ms_cubic-bezier(0.34,1.6,0.5,1)]"
        style={{ boxShadow: 'var(--shadow-stamp-lg), 0 30px 60px -20px rgba(0,0,0,0.35)' }}
      >
        <button
          type="button"
          onClick={() => dispatch(closeAccountModal())}
          aria-label={t('common.close')}
          className="absolute top-3 end-3 z-10 inline-flex w-9 h-9 items-center justify-center border-2 border-ink rounded-btn-sm bg-paper font-display text-xl transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px]"
        >×</button>
        {(mode === 'login' || mode === 'register') && (
          <div role="tablist" aria-label={t('account.modal.ariaLabel')} className="flex gap-1 border-b-2 border-ink shrink-0 pr-16 p-1">
            <Tab active={mode === 'login'} onClick={() => dispatch(setMode('login'))}>{t('account.modal.actions.login')}</Tab>
            <Tab active={mode === 'register'} onClick={() => dispatch(setMode('register'))}>{t('account.modal.titles.register')}</Tab>
          </div>
        )}
        <div className="overflow-y-auto px-5 py-4">
          {mode === 'login' && <LoginPanel />}
          {mode === 'register' && <RegisterPanel />}
          {mode === 'forgot' && <ForgotPanel />}
        </div>
      </div>
      <style>{`
        @keyframes modalOverlay { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

interface TabProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function Tab({ active, onClick, children }: TabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex-1 border-0 px-3 py-2.5 rounded-btn-sm font-display text-sm sm:text-base text-center cursor-pointer transition-colors ${
        active ? 'bg-ink text-paper' : 'bg-transparent text-ink/50 hover:text-ink hover:bg-cream'
      }`}
    >
      {children}
    </button>
  );
}
